(function () {
  'use strict';

  var VERSION = '2.0.0';
  var existingManager = window.GSJSellStepsStack;

  if (existingManager && existingManager.version === VERSION) {
    existingManager.scan(document);
    return;
  }

  if (existingManager && typeof existingManager.destroyAll === 'function') {
    existingManager.destroyAll();
  }

  var ROOT_SELECTOR = '[data-gsj-sell-steps-stack]';
  var ITEM_SELECTOR = '[data-gsj-sell-steps-item]';
  var CARD_SELECTOR = '[data-gsj-sell-steps-card]';
  var READY_CLASS = 'sell-steps-stack--js-ready';
  var MEDIA_READY_CLASS = 'sell-steps-stack--media-ready';
  var ACTIVE_CLASS = 'sell-steps-stack--active';
  var EDITOR_CLASS = 'sell-steps-stack--editor-selected';
  var instances = new Map();
  var frameId = 0;
  var measureFrameId = 0;
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function toNumber(value, fallback) {
    var parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function easeOutCubic(value) {
    return 1 - Math.pow(1 - value, 3);
  }

  function viewportHeight() {
    return document.documentElement.clientHeight || window.innerHeight || 1;
  }

  function safelyPlay(video) {
    if (!video || !video.paused || document.hidden) return;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
  }

  function safelyPause(video) {
    if (video && !video.paused) video.pause();
  }

  function configureVideo(video) {
    if (!video) return;
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
  }

  function isDisplayed(video) {
    return Boolean(video && window.getComputedStyle(video).display !== 'none');
  }

  function scheduleUpdate() {
    if (frameId) return;

    frameId = window.requestAnimationFrame(function () {
      frameId = 0;
      instances.forEach(function (instance) {
        if (instance.nearViewport) instance.update();
      });
    });
  }

  function scheduleMeasure() {
    if (measureFrameId) return;

    measureFrameId = window.requestAnimationFrame(function () {
      measureFrameId = 0;
      instances.forEach(function (instance) {
        instance.measure();
      });
      scheduleUpdate();
    });
  }

  var intersectionObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var instance = instances.get(entry.target);
            if (!instance) return;

            instance.nearViewport = entry.isIntersecting;
            instance.root.classList.toggle(ACTIVE_CLASS, entry.isIntersecting && instance.canEnhance());

            if (entry.isIntersecting) {
              instance.measure();
              scheduleUpdate();
            } else {
              instance.pauseAllVideos();
              instance.setDominantCard(-1, false, true);
            }
          });
        },
        { rootMargin: '100% 0px 100% 0px' }
      )
    : null;

  function SellStepsStack(root) {
    this.root = root;
    this.list = root.querySelector('[data-gsj-sell-steps-list]');
    this.items = this.list ? Array.from(this.list.querySelectorAll(ITEM_SELECTOR)) : [];
    this.cards = this.items.map(function (item) { return item.querySelector(CARD_SELECTOR); });
    this.sectionMedia = root.querySelector('[data-gsj-section-media]');
    this.sectionVideos = this.sectionMedia ? Array.from(this.sectionMedia.querySelectorAll('video')) : [];
    this.cardVideos = [];
    this.nearViewport = !intersectionObserver;
    this.editorSelected = false;
    this.forcedCardIndex = -1;
    this.dominantCardIndex = -2;
    this.nextVideoIndex = -1;
    this.sectionVideoPlaying = null;
    this.offsets = [];
    this.options = {};
    this.resizeObserver = null;
    this.onMediaLoad = this.onMediaLoad.bind(this);

    if (!this.list || this.cards.some(function (card) { return !card; })) return;

    this.cardVideos = this.cards.map(function (card) { return Array.from(card.querySelectorAll('video')); });

    this.sectionVideos.forEach(configureVideo);
    this.cardVideos.forEach(function (videos) { videos.forEach(configureVideo); });
    this.root.addEventListener('load', this.onMediaLoad, true);
    this.root.addEventListener('loadedmetadata', this.onMediaLoad, true);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(scheduleMeasure);
      this.resizeObserver.observe(this.list);
      this.cards.forEach(function (card) { this.resizeObserver.observe(card); }, this);
    }

    instances.set(this.root, this);
    this.readOptions();
    this.root.classList.add(MEDIA_READY_CLASS);
    this.root.classList.toggle(READY_CLASS, this.options.enabled && this.items.length > 1);

    if (intersectionObserver) {
      intersectionObserver.observe(this.root);
    } else {
      this.root.classList.toggle(ACTIVE_CLASS, this.canEnhance());
    }

    this.measure();
  }

  SellStepsStack.prototype.readOptions = function () {
    var data = this.root.dataset;
    var width = window.innerWidth;
    var height = viewportHeight();
    var baseScale = toNumber(data.baseScale, 88);
    var scaleStrength = toNumber(data.scaleStrength, 3.5);
    var rawStackPosition = toNumber(data.stackPosition, 18);
    var rawItemDistance = Math.max(toNumber(data.itemDistance, 100), 1);
    var rawStackDistance = toNumber(data.stackDistance, 28);
    var rawReleaseSpacing = toNumber(data.releaseSpacing, 35);

    if (baseScale > 1) baseScale /= 100;
    if (scaleStrength > 0.2) scaleStrength /= 100;

    var options = {
      enabled: data.enableScrollStack === 'true',
      stackPosition: rawStackPosition,
      itemDistance: rawItemDistance,
      stackDistance: rawStackDistance,
      releaseSpacing: rawReleaseSpacing,
      baseScale: clamp(baseScale, 0.5, 1),
      scaleStrength: Math.max(scaleStrength, 0),
      rotation: toNumber(data.rotationAmount, 0),
      blur: Math.max(toNumber(data.blurAmount, 0), 0),
      disableStackMobile: data.disableStackMobile === 'true',
      disableBlurMobile: data.disableBlurMobile === 'true',
      stickyOffsetDesktop: toNumber(data.stickyOffsetDesktop, 0),
      stickyOffsetTablet: toNumber(data.stickyOffsetTablet, 0),
      stickyOffsetMobile: toNumber(data.stickyOffsetMobile, 0),
      sectionParallax: toNumber(data.sectionMediaParallaxStrength, 0),
      pauseSectionVideoOffscreen: data.pauseSectionVideoOffscreen !== 'false',
      disableSectionVideoReducedMotion: data.disableSectionVideoReducedMotion !== 'false'
    };

    if (width <= 749) {
      options.stackPosition = Math.min(rawStackPosition, height < 640 ? 8 : 12);
      options.itemDistance = Math.min(rawItemDistance, height < 640 ? 56 : 68);
      options.stackDistance = Math.min(rawStackDistance, height < 640 ? 10 : 14);
      options.releaseSpacing = Math.min(rawReleaseSpacing, height < 640 ? 22 : 26);
      options.baseScale = Math.max(options.baseScale, 0.96);
      options.scaleStrength = Math.min(options.scaleStrength, 0.01);
      options.rotation = 0;
      options.blur = 0;
    } else if (width <= 989) {
      options.stackPosition = Math.min(rawStackPosition, height < 650 ? 8 : 14);
      options.itemDistance = Math.min(rawItemDistance, height < 650 ? 58 : 82);
      options.stackDistance = Math.min(rawStackDistance, height < 650 ? 12 : 19);
      options.releaseSpacing = Math.min(rawReleaseSpacing, height < 650 ? 22 : 30);
      options.baseScale = Math.max(options.baseScale, height < 650 ? 0.95 : 0.93);
      options.scaleStrength = Math.min(options.scaleStrength, height < 650 ? 0.012 : 0.02);
      options.rotation = height < 650 ? 0 : options.rotation * 0.5;
      options.blur = height < 650 ? 0 : Math.min(options.blur, 1);
    } else if (height < 720) {
      options.stackPosition = Math.min(rawStackPosition, 10);
      options.itemDistance = Math.min(rawItemDistance, 76);
      options.stackDistance = Math.min(rawStackDistance, 20);
      options.releaseSpacing = Math.min(rawReleaseSpacing, 28);
      options.baseScale = Math.max(options.baseScale, 0.92);
      options.scaleStrength = Math.min(options.scaleStrength, 0.022);
      options.rotation *= 0.65;
      options.blur = Math.min(options.blur, 1.5);
    } else if (height < 850) {
      options.stackPosition = Math.min(rawStackPosition, 14);
      options.itemDistance = Math.min(rawItemDistance, 88);
      options.stackDistance = Math.min(rawStackDistance, 24);
      options.releaseSpacing = Math.min(rawReleaseSpacing, 32);
      options.baseScale = Math.max(options.baseScale, 0.9);
      options.scaleStrength = Math.min(options.scaleStrength, 0.03);
    }

    this.options = options;

    var stackPositionPixels = height * (options.stackPosition / 100);
    this.root.style.setProperty('--sell-steps-stack-position', stackPositionPixels.toFixed(2) + 'px');
    this.root.style.setProperty('--sell-steps-item-distance', options.itemDistance + 'px');
    this.root.style.setProperty('--sell-steps-stack-distance', options.stackDistance + 'px');
    this.root.style.setProperty('--sell-steps-release-spacing', height * (options.releaseSpacing / 100) + 'px');

    this.items.forEach(function (item, index) {
      item.style.setProperty('--sell-steps-index', index);
      item.style.setProperty('--sell-steps-index-offset', index * options.stackDistance + 'px');
    });
  };

  SellStepsStack.prototype.canEnhance = function () {
    return Boolean(
      this.options.enabled &&
      this.items.length > 1 &&
      !this.editorSelected &&
      !reducedMotion.matches &&
      !(window.innerWidth <= 749 && this.options.disableStackMobile)
    );
  };

  SellStepsStack.prototype.getStickyOffset = function () {
    if (window.innerWidth <= 749) return this.options.stickyOffsetMobile;
    if (window.innerWidth <= 989) return this.options.stickyOffsetTablet;
    return this.options.stickyOffsetDesktop;
  };

  SellStepsStack.prototype.measure = function () {
    if (!this.list || !this.root.isConnected) return;

    this.readOptions();
    this.sectionVideoPlaying = null;
    this.dominantCardIndex = -2;
    this.root.classList.toggle(READY_CLASS, this.options.enabled && this.items.length > 1);

    if (!this.canEnhance()) {
      this.root.classList.remove(ACTIVE_CLASS);
      this.resetCards();
      this.syncMediaPlayback(this.editorSelected ? Math.max(this.forcedCardIndex, 0) : -1, this.editorSelected, true);
      return;
    }

    var naturalOffset = 0;
    this.offsets = this.items.map(function (item, index) {
      if (index > 0) naturalOffset += this.items[index - 1].offsetHeight + this.options.itemDistance;
      return naturalOffset;
    }, this);
    this.root.classList.toggle(ACTIVE_CLASS, this.nearViewport);
  };

  SellStepsStack.prototype.update = function () {
    if (!this.canEnhance() || !this.offsets.length) {
      this.resetCards();
      return;
    }

    var height = viewportHeight();
    var listTop = this.list.getBoundingClientRect().top;
    var rootRect = this.root.getBoundingClientRect();
    var sectionVisible = rootRect.bottom > 0 && rootRect.top < height;
    var stickyBase = height * (this.options.stackPosition / 100) + this.getStickyOffset();
    var dominantIndex = 0;
    var writes = this.cards.map(function (card, index) {
      var progress = 0;

      if (index < this.cards.length - 1) {
        var nextIndex = index + 1;
        var nextNaturalTop = listTop + this.offsets[nextIndex];
        var nextStickyTop = stickyBase + nextIndex * this.options.stackDistance;
        var rawProgress = (nextStickyTop + this.options.itemDistance - nextNaturalTop) / this.options.itemDistance;
        progress = easeOutCubic(clamp(rawProgress, 0, 1));
      }

      if (index > 0) {
        var activationTop = stickyBase + index * this.options.stackDistance + this.options.itemDistance * 0.45;
        if (listTop + this.offsets[index] <= activationTop) dominantIndex = index;
      }

      var targetScale = clamp(
        this.options.baseScale + index * this.options.scaleStrength,
        this.options.baseScale,
        1
      );
      var direction = index % 2 === 0 ? -1 : 1;
      var mediaShift = 0;

      if (card.dataset.cardMediaParallax === 'true' && !reducedMotion.matches) {
        var naturalTop = listTop + this.offsets[index];
        var mediaProgress = clamp((height - naturalTop) / (height + this.items[index].offsetHeight), 0, 1);
        mediaShift = (mediaProgress - 0.5) * 22;
      }

      return {
        card: card,
        scale: index === this.cards.length - 1 ? 1 : 1 - (1 - targetScale) * progress,
        rotation: index === this.cards.length - 1 ? 0 : this.options.rotation * direction * progress,
        blur: index === this.cards.length - 1 ? 0 : this.options.blur * progress,
        mediaShift: mediaShift
      };
    }, this);

    writes.forEach(function (state) {
      state.card.style.transform =
        'scale(' + state.scale.toFixed(4) + ') rotate(' + state.rotation.toFixed(3) + 'deg)';
      state.card.style.filter = state.blur > 0.01 ? 'blur(' + state.blur.toFixed(2) + 'px)' : 'none';
      state.card.style.setProperty('--sell-steps-card-media-shift', state.mediaShift.toFixed(2) + 'px');
    });

    if (this.sectionMedia && this.options.sectionParallax > 0 && !reducedMotion.matches) {
      var sectionProgress = clamp((height - rootRect.top) / (height + rootRect.height), 0, 1);
      var sectionShift = (sectionProgress - 0.5) * height * (this.options.sectionParallax / 100);
      this.root.style.setProperty('--sell-steps-section-media-shift', sectionShift.toFixed(2) + 'px');
    } else {
      this.root.style.setProperty('--sell-steps-section-media-shift', '0px');
    }

    var nextVideoIndex = -1;
    if (sectionVisible && dominantIndex < this.cards.length - 1) {
      var nextNaturalPosition = listTop + this.offsets[dominantIndex + 1];
      if (nextNaturalPosition <= height * 1.15) nextVideoIndex = dominantIndex + 1;
    }

    this.setDominantCard(sectionVisible ? dominantIndex : -1, sectionVisible, false, nextVideoIndex);
  };

  SellStepsStack.prototype.setDominantCard = function (index, sectionVisible, force, nextVideoIndex) {
    var nextIndex = typeof nextVideoIndex === 'number' ? nextVideoIndex : -1;

    if (!force && index === this.dominantCardIndex && nextIndex === this.nextVideoIndex) {
      this.syncSectionVideo(sectionVisible);
      return;
    }

    this.dominantCardIndex = index;
    this.nextVideoIndex = nextIndex;
    this.cards.forEach(function (card, cardIndex) {
      card.classList.toggle('is-stack-active', cardIndex === index);
      card.classList.toggle('is-stack-past', index >= 0 && cardIndex < index);
      card.classList.toggle('is-stack-next', index >= 0 && cardIndex === index + 1);
    });
    this.syncMediaPlayback(index, sectionVisible, true);
  };

  SellStepsStack.prototype.syncSectionVideo = function (sectionVisible) {
    var shouldPlay = Boolean(
      !document.hidden &&
      (!this.options.pauseSectionVideoOffscreen || sectionVisible) &&
      !(reducedMotion.matches && this.options.disableSectionVideoReducedMotion)
    );

    if (shouldPlay === this.sectionVideoPlaying) return;
    this.sectionVideoPlaying = shouldPlay;

    this.sectionVideos.forEach(function (video) {
      if (shouldPlay && isDisplayed(video)) safelyPlay(video);
      else safelyPause(video);
    });
  };

  SellStepsStack.prototype.syncMediaPlayback = function (activeIndex, sectionVisible, force) {
    this.syncSectionVideo(sectionVisible);

    this.cardVideos.forEach(function (videos, index) {
      var card = this.cards[index];
      var allowNext = card.dataset.pauseCardVideoWhenInactive === 'false' && index === this.nextVideoIndex;
      var shouldPlay = Boolean(
        !document.hidden &&
        sectionVisible &&
        !reducedMotion.matches &&
        (index === activeIndex || allowNext)
      );

      videos.forEach(function (video) {
        if (shouldPlay && isDisplayed(video)) safelyPlay(video);
        else safelyPause(video);
      });
    }, this);
  };

  SellStepsStack.prototype.pauseAllVideos = function () {
    this.sectionVideos.forEach(safelyPause);
    this.cardVideos.forEach(function (videos) { videos.forEach(safelyPause); });
    this.sectionVideoPlaying = false;
  };

  SellStepsStack.prototype.resetCards = function () {
    this.cards.forEach(function (card) {
      card.style.removeProperty('transform');
      card.style.removeProperty('filter');
      card.style.removeProperty('--sell-steps-card-media-shift');
      card.classList.remove('is-stack-active', 'is-stack-past', 'is-stack-next');
    });
    this.root.style.setProperty('--sell-steps-section-media-shift', '0px');
    this.dominantCardIndex = -2;
    this.nextVideoIndex = -1;
  };

  SellStepsStack.prototype.onMediaLoad = function () {
    this.measure();
    scheduleUpdate();
  };

  SellStepsStack.prototype.setEditorSelected = function (selected, cardIndex) {
    this.editorSelected = selected;
    this.forcedCardIndex = selected ? clamp(toNumber(cardIndex, 0), 0, this.cards.length - 1) : -1;
    this.root.classList.toggle(EDITOR_CLASS, selected);
    this.resetCards();
    this.measure();

    if (selected) {
      this.setDominantCard(this.forcedCardIndex, true, true);
      var item = this.items[this.forcedCardIndex];
      if (item && window.Shopify && window.Shopify.designMode) {
        item.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
      }
    } else {
      scheduleUpdate();
    }
  };

  SellStepsStack.prototype.destroy = function () {
    if (intersectionObserver) intersectionObserver.unobserve(this.root);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    this.root.removeEventListener('load', this.onMediaLoad, true);
    this.root.removeEventListener('loadedmetadata', this.onMediaLoad, true);
    this.pauseAllVideos();
    this.resetCards();
    this.root.classList.remove(READY_CLASS, MEDIA_READY_CLASS, ACTIVE_CLASS, EDITOR_CLASS);
    this.root.style.removeProperty('--sell-steps-stack-position');
    this.root.style.removeProperty('--sell-steps-item-distance');
    this.root.style.removeProperty('--sell-steps-stack-distance');
    this.root.style.removeProperty('--sell-steps-release-spacing');
    this.root.style.removeProperty('--sell-steps-section-media-shift');
    instances.delete(this.root);

    if (!instances.size) {
      if (frameId) window.cancelAnimationFrame(frameId);
      if (measureFrameId) window.cancelAnimationFrame(measureFrameId);
      frameId = 0;
      measureFrameId = 0;
    }
  };

  function scan(scope) {
    if (!scope) return;

    if (scope.matches && scope.matches(ROOT_SELECTOR) && !instances.has(scope)) new SellStepsStack(scope);
    if (!scope.querySelectorAll) return;

    scope.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      if (!instances.has(root)) new SellStepsStack(root);
    });
    scheduleMeasure();
  }

  function destroyWithin(scope) {
    Array.from(instances.values()).forEach(function (instance) {
      if (scope === instance.root || (scope.contains && scope.contains(instance.root))) instance.destroy();
    });
  }

  function destroyAll() {
    Array.from(instances.values()).forEach(function (instance) { instance.destroy(); });
  }

  function setEditorState(event, selected) {
    instances.forEach(function (instance) {
      var target = event.target;
      if (
        target === instance.root ||
        instance.root.contains(target) ||
        (target.contains && target.contains(instance.root))
      ) {
        var selectedCard = target.closest ? target.closest(CARD_SELECTOR) : null;
        var selectedIndex = selectedCard ? instance.cards.indexOf(selectedCard) : 0;
        instance.setEditorSelected(selected, selectedIndex);
      }
    });
  }

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleMeasure, { passive: true });
  window.addEventListener('orientationchange', scheduleMeasure, { passive: true });
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) instances.forEach(function (instance) { instance.pauseAllVideos(); });
    else {
      instances.forEach(function (instance) {
        instance.dominantCardIndex = -2;
        instance.sectionVideoPlaying = null;
      });
      scheduleUpdate();
    }
  });

  if (typeof reducedMotion.addEventListener === 'function') reducedMotion.addEventListener('change', scheduleMeasure);
  else if (typeof reducedMotion.addListener === 'function') reducedMotion.addListener(scheduleMeasure);

  document.addEventListener('shopify:section:load', function (event) { scan(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroyWithin(event.target); });
  document.addEventListener('shopify:section:reorder', scheduleMeasure);
  document.addEventListener('shopify:section:select', function (event) { setEditorState(event, true); });
  document.addEventListener('shopify:section:deselect', function (event) { setEditorState(event, false); });
  document.addEventListener('shopify:block:select', function (event) { setEditorState(event, true); });
  document.addEventListener('shopify:block:deselect', function (event) { setEditorState(event, false); });

  if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleMeasure);

  window.GSJSellStepsStack = {
    version: VERSION,
    scan: scan,
    refresh: scheduleMeasure,
    destroyWithin: destroyWithin,
    destroyAll: destroyAll
  };

  scan(document);
})();
